import { useMediaQuery, TableContainer, Paper, TableHead, TableRow, TableBody, Theme } from "@mui/material"
import { useState } from "react"
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog"
import { ISliderInterface } from "../components/SliderCell"
import SliderRow from "../components/SliderRow"
import SwitchCell from "../components/SwitchCell"
import TextCell from "../components/TextCell"
import { SettingTable, TableCellButton, DefaultButton } from "../Settings"

interface ITrackingProps {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string, basic?: boolean) => void,
    setOption: (key: string, value: string | number | boolean, basic?: boolean) => void
    findFVBooleanSetting: (name: string) => boolean
    allowFaceToDisappear: boolean
}

const Tracking: React.FC<ITrackingProps> = (props) => {
    
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)

    const setDisallowMultipleFaces = (bool: boolean) => {
        props.setOption("DisallowMultipleFaces", bool)
    }

    const setAllowFaceToDisappear = (bool: boolean) => {
        props.setOption("AllowFaceToDisappear", bool)
    }

    const handleResetClick = () => {
        props.resetValueToDefault("DisallowMultipleFaces")
        props.resetValueToDefault("AllowFaceToDisappear")
        props.resetValueToDefault("TokenFaceBackgroundPercentage")
        props.resetValueToDefault("StreamMinimalInterOcularDistance")
        props.resetValueToDefault("TrackerRedetectPeriod")
        props.resetValueToDefault("IcaoMaximalRoll")
        props.resetValueToDefault("IcaoMaximalYaw")
        props.resetValueToDefault("IcaoMaximalPitch")
    }

    return (
        <>
        <TableContainer component={Paper} style={{marginBottom: "2rem", tableLayout: "fixed"}}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell head title="Tracking" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 2 : 3}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TextCell title="Disallow multiple faces" subtitle="Fails the operation if multiple faces are detected."/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={props.findFVBooleanSetting("DisallowMultipleFaces")} setState={setDisallowMultipleFaces}/>
                    </TableRow>
                    <TableRow>
                        <TextCell title="Allow face to disappear" subtitle="During stream operation allows face to leave image frame without stopping same stream session."/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={props.findFVBooleanSetting("AllowFaceToDisappear")} setState={setAllowFaceToDisappear}/>
                    </TableRow>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Token face background"
                        subtitle="Regulates the minimum and maximum face distance thus forcing face to position in frame at the right distance and place."
                        title="Threshold range"
                        option={props.findFVSliderInterface("TokenFaceBackgroundPercentage")}
                        defaultOption={props.findDefaultFVSliderInterface("TokenFaceBackgroundPercentage")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Stream minimal inter ocular distance"
                        subtitle="Minimum distance required for faces to be suitable for attributes, liveness, quality etc checks."
                        title="Pixels"
                        option={props.findFVSliderInterface("StreamMinimalInterOcularDistance")}
                        defaultOption={props.findDefaultFVSliderInterface("StreamMinimalInterOcularDistance")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={32}
                        maxValue={1000}
                        type="raising"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Tracker redetect period"
                        subtitle="Frame count value after which face detection algorithm will be called forcefully. By default detector works automatically."
                        title="Frames"
                        option={props.findFVSliderInterface("TrackerRedetectPeriod")}
                        defaultOption={props.findDefaultFVSliderInterface("TrackerRedetectPeriod")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={1000}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Tracking maximal roll"
                        subtitle="Used during face tracking stage and sets a threshold to rejects faces that have higher roll value."
                        title="Angle"
                        option={props.findFVSliderInterface("IcaoMaximalRoll")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoMaximalRoll")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={180}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Tracking maximal yaw"
                        subtitle="Used during face tracking stage and sets a threshold to rejects faces that have higher yaw value."
                        title="Angle"
                        option={props.findFVSliderInterface("IcaoMaximalYaw")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoMaximalYaw")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={180}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Tracking maximal pitch"
                        subtitle="Used during face tracking stage and sets a threshold to rejects faces that have higher pitch value."
                        title="Angle"
                        option={props.findFVSliderInterface("IcaoMaximalPitch")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoMaximalPitch")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={180}
                        type="falling"
                        active
                        lockMaxValue
                        dontShowWarningMsg
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset ICAO settings"
            text="Reset ICAO settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    )
}

export default Tracking